import { useState } from 'react';
import dayjs from 'dayjs';
import type { FetchWithAuth } from '../../auth';

/**
 * @param name
 * @param type
 */
type FileMetadata = {
  name: string;
  type: string;
};

/**
 * @param url request url to perform a GET request
 * @param token JWT token used in auth header to access protected resource
 * @param file file metadata such as name and type
 */
export type DownloadBlobConfig = {
  url: string;
  token: string;
  file?: FileMetadata;
  fetchWithAuth: FetchWithAuth
};

/**
 * @param Blob representation of object or file
 * @param filename name of blob
 */
export type DownloadableBlob = {
  blob: Blob;
  filename: string;
};

/**
 * @param loading current loading state fetch request
 * @param downloadBlob method to initiate fetch request and returns blob of object or file
 */
export type UseDownloadBlob = {
  loading: boolean;
  /**
   * @param config details required for request such as url and token
   * @returns blob of object or file and name
   */
  downloadBlob: (config: DownloadBlobConfig) => Promise<DownloadableBlob>;
};

/**
 * This method takes a Content-Disposition response header on the form:
 * `Content-Disposition: attachment; filename="filename.jpg"`
 *
 * It then uses REGEX to extract the filename component, in this example: `filename.jpg`
 * @param attachment Content-Disposition header
 * @returns filename
 */
const getFilenameFromAttachment = (attachment: string): string => {
  const extractedFilename = attachment
    .replace('attachment; filename=', '')
    .replace(/"/g, '');

  return extractedFilename;
};

/**
 * This method returns the filename of a given file from its metadata or
 * `Content-Disposition: attachment` response header, which ever is provided.
 *
 * If neither is present, it generates a filename in the format:
 *
 * download-YYYY-MM-DD (current date)
 *
 * @param attachment Content-Disposition header
 * @param file file metadata
 * @returns filename
 */
const getFilename = (
  attachment: string | null,
  file?: FileMetadata
): string => {
  if (file) {
    return `${file.name}.${file.type}`;
  }

  if (attachment) {
    return getFilenameFromAttachment(attachment);
  }

  return `download-${dayjs().format('YYYY-MM-DD')}`;
};

/**
 * This hook provides a means of downloading an object or file as a Blob
 * @returns a method to initiate download fetch request and current loading state
 * of request
 */
export const useDownloadBlob = (): UseDownloadBlob => {
  const [loading, setLoading] = useState(false);

  const downloadBlob = async ({
    url,
    token,
    file,
    fetchWithAuth
  }: DownloadBlobConfig): Promise<DownloadableBlob> => {
    try {
      setLoading(true);
      const response = await fetchWithAuth(url, {
        method: 'GET',
        mode: 'cors',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(response.statusText);
      }

      const attachment = response.headers.get('Content-Disposition');
      const blob = await response.blob();
      const name = getFilename(attachment, file);

      return { blob, filename: name };
    } catch (e) {
      if (e instanceof Error) throw new Error(e.message);
      else throw e;
    } finally {
      setLoading(false);
    }
  };

  return { downloadBlob, loading };
};
